import { AudioWaveform, Play, Unplug } from "lucide-react";
import * as React from "react";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { languageName } from "@/lib/lead-card";
import { useTenants } from "@/lib/queries";
import { cn, relativeTime } from "@/lib/utils";
import {
  VOICE_GENDERS, VOICE_TEST_MAX, VOICE_TEST_SENTENCES, chooserVoices, goneVoices, greetingWarning,
  modelChoices, modelCost,
  modelLacks, ownVoicesText, repName, slotName, speakerFor, speakerText, testProblem, voiceChanged,
  voiceGenderLabel,
  voiceLine, voicePayload, voiceProblem, voiceSample, voiceValues, voicesSet, withVoice,
  type ElevenLabsVoice, type VoiceCatalog, type VoiceGender, type VoicePick, type VoiceSettings,
  type VoiceValues,
} from "@/lib/voice-settings";
import {
  useDisconnectVoices, useSaveVoiceSettings, useVoiceCatalog, useVoiceSettings, useVoiceTest,
} from "@/lib/voice-settings-api";
import { VOICEMAIL_LANGUAGES } from "@/lib/voicemail";

/**
 * Voices (ADR-114 in platform): who speaks the tenant's voicemail greetings, on one screen.
 *
 * Top to bottom is the order it is set up in. The ElevenLabs account: its API key and the model. A
 * voice for each language a greeting is written in, for a female and for a male rep, picked from the
 * account's own voices, each with a sample to hear. Then who speaks in every case, as core-api's
 * rule has it for the settings that are saved, and a sentence to hear in any of them.
 *
 * The voices and the test come once a key is saved: both need the account. From the first voice
 * that is saved, a greeting written as text is left on calls (ADR-128 in platform). When ElevenLabs
 * could not speak one, the page says so first, in core-api's sentence (`greetingWarning`).
 *
 * Admins only, as core-api's routes are. A superadmin with more than one tenant picks the tenant.
 */
export function VoicesPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const tenants = useTenants({ enabled: isSuperadmin });
  const pickTenant = isSuperadmin && (tenants.data?.length ?? 0) > 1;
  const [picked, setPicked] = React.useState<number>();
  const tenantId = pickTenant ? (picked ?? tenants.data?.[0]?.id) : undefined;
  // A superadmin's own tenant is nobody's: wait to know which tenants there are.
  const settings = useVoiceSettings(tenantId, !isSuperadmin || !tenants.isPending);

  return (
    <Page>
      <PageHeader
        title="Voices"
        description="Who speaks your voicemail greetings: your ElevenLabs account, and a voice for each language and for a female and a male rep."
        action={
          pickTenant && (
            <Select value={String(tenantId)} onValueChange={(value) => setPicked(Number(value))}>
              <SelectTrigger aria-label="Tenant" className="w-52">
                <SelectValue placeholder="Choose tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.data?.map((tenant) => (
                  <SelectItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />
      {settings.isPending ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : settings.error || !settings.data ? (
        <ErrorState
          className="min-h-[40vh]"
          error={settings.error}
          title="Could not load the voice settings"
          action={
            <Button variant="outline" onClick={() => void settings.refetch()}>
              Try again
            </Button>
          }
        />
      ) : (
        // Keyed by the tenant and by when the settings were last saved: the form is loaded again for
        // another tenant and after a save, and not each time the settings are fetched.
        <VoicesBody
          key={`${settings.data.tenant_id}:${settings.data.updated_at ?? "new"}`}
          settings={settings.data}
          tenantId={tenantId}
        />
      )}
    </Page>
  );
}

function VoicesBody({ settings, tenantId }: { settings: VoiceSettings; tenantId: number | undefined }) {
  const toast = useToast();
  const save = useSaveVoiceSettings(tenantId);
  const disconnect = useDisconnectVoices(tenantId);
  const catalog = useVoiceCatalog(tenantId, settings.api_key_set);
  // What the form was loaded with, which a change is measured against.
  const [loaded] = React.useState<VoiceValues>(() => voiceValues(settings));
  const [values, setValues] = React.useState<VoiceValues>(loaded);
  const [touched, setTouched] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [disconnecting, setDisconnecting] = React.useState(false);

  const change = (next: VoiceValues) => {
    setValues(next);
    setSaveError(null);
  };
  const problem = voiceProblem(values, settings);
  const changed = voiceChanged(values, loaded);
  const kept = settings.api_key_set;
  const models = modelChoices(catalog.data?.models ?? [], values.modelId);
  const model = models.find((candidate) => candidate.model_id === values.modelId);
  const lacking = modelLacks(model);
  const gone = goneVoices(values.voices, catalog.data);
  const warning = greetingWarning(settings);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (problem) return;
    setSaveError(null);
    try {
      const saved = await save.mutateAsync(voicePayload(values));
      const count = voicesSet(voiceValues(saved).voices);
      toast.success(
        "Voice settings saved",
        count === 0
          ? "ElevenLabs accepted the key. Pick the voices next."
          : `ElevenLabs accepted the key and ${count === 1 ? "the voice" : `the ${count} voices`}.`,
      );
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : "Could not save the settings. Please try again.");
    }
  };

  const remove = async () => {
    try {
      await disconnect.mutateAsync();
      toast.success("Disconnected from ElevenLabs", "The key and the voices are removed here.");
    } catch (error) {
      toast.error("Could not disconnect", error instanceof ApiError ? error.message : "Please try again.");
    }
    setDisconnecting(false);
  };

  return (
    <div className="space-y-4">
      {warning && (
        <Callout tone="warning">
          <p className="font-medium">
            ElevenLabs could not speak a greeting
            {settings.last_problem_at ? ` ${relativeTime(settings.last_problem_at)}` : ""}.
          </p>
          <p className="mt-0.5">{warning}</p>
        </Callout>
      )}
      <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
        <Card className="overflow-hidden">
          <CardHeader
            title="ElevenLabs account"
            description={
              kept && settings.checked_at
                ? `ElevenLabs accepted the key ${relativeTime(settings.checked_at)}.`
                : "The account that speaks your greetings, and pays for each character spoken."
            }
            action={
              kept && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setDisconnecting(true)}>
                  <Unplug />
                  Disconnect
                </Button>
              )
            }
          />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Field
              label={kept ? "Replace the API key" : "API key"}
              htmlFor="voice-api-key"
              required={!kept}
              hint={
                kept
                  ? "A key is kept. Leave blank to keep it."
                  : "From ElevenLabs, under Developers, API keys. Checked when you save, and never shown again."
              }
            >
              <Input
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                value={values.apiKey}
                onChange={(event) => change({ ...values, apiKey: event.target.value })}
              />
            </Field>
            {kept && (
              <Field
                label="Model"
                htmlFor="voice-model"
                hint={
                  lacking.length > 0
                    ? `ElevenLabs does not list ${lacking.join(" or ")} for this model.`
                    : catalog.data && catalog.data.models.length === 0
                      ? "This key may not read the models, so only the saved one is listed."
                      : [model?.description, modelCost(model?.cost ?? null)].filter(Boolean).join(" ")
                        || "The ElevenLabs model every greeting is spoken with."
                }
              >
                <Select value={values.modelId} onValueChange={(modelId) => change({ ...values, modelId })}>
                  {/* The id is said again here: `Field` gives it to `Select`, which draws nothing. */}
                  <SelectTrigger id="voice-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((candidate) => (
                      <SelectItem
                        key={candidate.model_id}
                        value={candidate.model_id}
                        description={modelCost(candidate.cost) || undefined}
                      >
                        {candidate.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </CardBody>
          {!kept && (
            <VoicesFooter
              note="ElevenLabs is asked about the key when you save. Nothing is saved if it says no."
              problem={touched ? problem : null}
              saveError={saveError}
              saving={save.isPending}
              changed={changed}
            />
          )}
        </Card>

        {kept && (
          <Card className="overflow-hidden">
            <CardHeader
              title="Voices"
              description="A voice for a female rep and one for a male rep, in each language. The voice says the rep's name, so it should fit the rep."
            />
            <CardBody className="space-y-5">
              {catalog.error && (
                <Callout tone="danger">
                  <p>
                    {catalog.error instanceof ApiError
                      ? catalog.error.message
                      : "Could not read your ElevenLabs account."}{" "}
                    The voices cannot be listed.
                  </p>
                  <button type="button" className="mt-1 underline" onClick={() => void catalog.refetch()}>
                    Try again
                  </button>
                </Callout>
              )}
              {gone.length > 0 && (
                <Callout tone="warning">
                  {gone.length === 1 ? `The ${gone[0]} voice is` : `The ${gone.join(" and the ")} voices are`} not in
                  your ElevenLabs account any more. Choose another.
                </Callout>
              )}
              {VOICEMAIL_LANGUAGES.map((language) => (
                <div key={language} className="space-y-2">
                  <p className="text-sm font-medium text-text">{languageName(language)} greetings</p>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {VOICE_GENDERS.map((gender) => (
                      <VoiceSlot
                        key={gender}
                        language={language}
                        gender={gender}
                        pick={values.voices[language]?.[gender] ?? null}
                        catalog={catalog.data}
                        loading={catalog.isPending}
                        failed={Boolean(catalog.error)}
                        onPick={(pick) => change(withVoice(values, language, gender, pick))}
                      />
                    ))}
                  </div>
                </div>
              ))}

              <div className="space-y-2">
                <p id="voice-default-label" className="text-sm font-medium text-text">
                  A rep with no voice set
                </p>
                <div role="radiogroup" aria-labelledby="voice-default-label" className="grid gap-2 sm:grid-cols-2">
                  {VOICE_GENDERS.map((gender) => {
                    const selected = values.defaultGender === gender;
                    return (
                      <button
                        key={gender}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => change({ ...values, defaultGender: gender })}
                        className={cn(
                          "rounded-md border px-3 py-2 text-left transition-colors",
                          selected
                            ? "border-accent-8 bg-accent-2 ring-2 ring-accent-8/25"
                            : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
                        )}
                      >
                        <span className="block text-sm font-medium text-text">
                          Speaks with the {gender} voice
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-text-muted">
                  This is for a person whose account says neither female nor male. An admin sets a person's
                  voice on the Users page, and each person can set their own under Account.
                </p>
              </div>
            </CardBody>
            <VoicesFooter
              note="ElevenLabs is asked about the key and every voice when you save. Nothing is saved if it says no."
              problem={touched ? problem : null}
              saveError={saveError}
              saving={save.isPending}
              changed={changed}
            />
          </Card>
        )}
      </form>

      {kept && <WhoSpeaksCard settings={settings} tenantId={tenantId} unsaved={changed} />}

      <ConfirmDialog
        open={disconnecting}
        onOpenChange={setDisconnecting}
        title="Disconnect from ElevenLabs?"
        description="The API key and every voice picked are removed here. Nothing changes at ElevenLabs: the key still works there until you revoke it there."
        confirmLabel="Disconnect"
        destructive
        loading={disconnect.isPending}
        onConfirm={() => void remove()}
      />
    </div>
  );
}

function VoicesFooter({
  note, problem, saveError, saving, changed,
}: {
  note: string;
  problem: string | null;
  saveError: string | null;
  saving: boolean;
  changed: boolean;
}) {
  return (
    <>
      {(problem || saveError) && (
        <div className="space-y-2 px-4 pb-3">
          {problem && <Callout tone="danger">{problem}</Callout>}
          {saveError && <Callout tone="danger">{saveError}</Callout>}
        </div>
      )}
      <CardFooter className="justify-between">
        <span className="text-xs text-text-muted">{note}</span>
        <Button type="submit" variant="default" loading={saving} disabled={!changed}>
          Save
        </Button>
      </CardFooter>
    </>
  );
}

/** One slot: the voice picked for a language and a kind of rep, with its sample, or nothing yet. */
function VoiceSlot({
  language, gender, pick, catalog, loading, failed, onPick,
}: {
  language: string;
  gender: VoiceGender;
  pick: VoicePick | null;
  catalog: VoiceCatalog | undefined;
  loading: boolean;
  failed: boolean;
  onPick: (pick: VoicePick | null) => void;
}) {
  const [choosing, setChoosing] = React.useState(false);
  const voice = pick ? catalog?.voices.find((candidate) => candidate.voice_id === pick.voice_id) : undefined;
  const sample = voice ? voiceSample(voice, language) : null;
  const what = slotName(language, gender);
  const summaryId = React.useId();

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
      <div id={summaryId} className="min-w-0 flex-1">
        <p className="text-xs text-text-muted">{voiceGenderLabel(gender)} rep</p>
        {pick ? (
          <>
            <p className="truncate text-sm font-medium text-text">{pick.name}</p>
            {voice && <p className="truncate text-xs text-text-muted">{voiceLine(voice)}</p>}
          </>
        ) : (
          <p className="text-sm text-text-muted">No voice yet</p>
        )}
      </div>
      {sample && (
        // `preload="none"`: four slots do not fetch four samples just to show them.
        <audio
          src={sample}
          controls
          preload="none"
          aria-label={`Listen to ${pick?.name}, the ${what} voice`}
          className="h-8 w-44 max-w-full"
        />
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-describedby={summaryId}
        aria-label={`${pick ? "Change" : "Choose"} the ${what} voice`}
        disabled={loading || failed || !catalog}
        onClick={() => setChoosing(true)}
      >
        <AudioWaveform />
        {pick ? "Change" : "Choose"}
      </Button>
      {pick && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Clear the ${what} voice`}
          onClick={() => onPick(null)}
        >
          Clear
        </Button>
      )}
      {catalog && (
        <VoiceChooser
          open={choosing}
          onOpenChange={setChoosing}
          language={language}
          gender={gender}
          catalog={catalog}
          current={pick?.voice_id ?? null}
          onPick={(chosen) => {
            onPick({ voice_id: chosen.voice_id, name: chosen.name });
            setChoosing(false);
          }}
        />
      )}
    </div>
  );
}

function VoiceChooser({
  open, onOpenChange, language, gender, catalog, current, onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  language: string;
  gender: VoiceGender;
  catalog: VoiceCatalog;
  current: string | null;
  onPick: (voice: ElevenLabsVoice) => void;
}) {
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    if (open) setSearch("");
  }, [open]);
  const { fitting, others } = chooserVoices(catalog.voices, language, gender, search);
  const what = slotName(language, gender);

  const list = (voices: ElevenLabsVoice[]) => (
    <ul className="space-y-1.5">
      {voices.map((voice) => {
        const chosen = voice.voice_id === current;
        const sample = voiceSample(voice, language);
        return (
          <li
            key={voice.voice_id}
            className={cn(
              "flex flex-wrap items-center gap-2 rounded-md border px-3 py-2",
              chosen ? "border-accent-8 bg-accent-2" : "border-border",
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-text">{voice.name}</p>
              <p className="truncate text-xs text-text-muted">{voiceLine(voice) || "No labels at ElevenLabs"}</p>
            </div>
            {sample ? (
              <audio
                src={sample}
                controls
                preload="none"
                aria-label={`Listen to ${voice.name}`}
                className="h-8 w-44 max-w-full"
              />
            ) : (
              <span className="text-xs text-text-subtle">No sample</span>
            )}
            <Button
              type="button"
              size="sm"
              variant={chosen ? "outline" : "default"}
              disabled={chosen}
              aria-label={chosen ? `${voice.name} is chosen` : `Use ${voice.name}`}
              onClick={() => onPick(voice)}
            >
              {chosen ? "Chosen" : "Use"}
            </Button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose the {what} voice</DialogTitle>
          <DialogDescription>
            The voices of your ElevenLabs account. It speaks {languageName(language)} greetings in the name
            of a {gender} rep.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Input
            aria-label="Search the voices"
            placeholder="Search by name, accent or description"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {fitting.length + others.length === 0 ? (
            <EmptyState
              icon={AudioWaveform}
              title={search ? "No voice matches" : "The account has no voices"}
              description={search ? "Try another word." : "Add a voice at ElevenLabs first."}
            />
          ) : (
            <div className="max-h-[28rem] space-y-3 overflow-y-auto">
              {fitting.length > 0 && (
                <section aria-label={`${voiceGenderLabel(gender)} voices`} className="space-y-1.5">
                  <p className="text-xs font-medium text-text">{voiceGenderLabel(gender)} voices</p>
                  {list(fitting)}
                </section>
              )}
              {others.length > 0 && (
                <section aria-label="Other voices" className="space-y-1.5">
                  <p className="text-xs font-medium text-text">
                    Other voices
                    <span className="ml-1 font-normal text-text-muted">
                      Not labelled {gender} at ElevenLabs.
                    </span>
                  </p>
                  {list(others)}
                </section>
              )}
            </div>
          )}
          {catalog.more_voices && (
            <p className="text-xs text-text-muted">
              Your account has more voices than are listed here. The first {catalog.voices.length} are shown.
            </p>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

const REPS: (VoiceGender | null)[] = [...VOICE_GENDERS, null];

/**
 * Who speaks in every case, for the settings as they are saved, and a sentence to hear in any of
 * them. core-api decides who speaks, by the rule the calls will use: the console only shows it.
 */
function WhoSpeaksCard({
  settings, tenantId, unsaved,
}: {
  settings: VoiceSettings;
  tenantId: number | undefined;
  unsaved: boolean;
}) {
  const test = useVoiceTest(tenantId);
  const [language, setLanguage] = React.useState<string>(VOICEMAIL_LANGUAGES[0]);
  const [rep, setRep] = React.useState<VoiceGender | null>(null);
  const [text, setText] = React.useState(VOICE_TEST_SENTENCES[language] ?? "");
  const [touched, setTouched] = React.useState(false);
  const [sound, setSound] = React.useState<{ url: string; by: string } | null>(null);
  // The address of a sound is this page's to give back.
  React.useEffect(() => () => {
    if (sound) URL.revokeObjectURL(sound.url);
  }, [sound]);

  const speaker = speakerFor(settings, language, rep);
  const nobody = !speaker?.voice_id;
  const problem = testProblem(text);
  const length = text.trim().replace(/\s+/g, " ").length;

  const pickLanguage = (next: string) => {
    // The starting sentence follows the language, until somebody has typed their own.
    if (text === (VOICE_TEST_SENTENCES[language] ?? "")) setText(VOICE_TEST_SENTENCES[next] ?? "");
    setLanguage(next);
  };

  const speak = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (problem || nobody || unsaved) return;
    try {
      const blob = await test.mutateAsync({ text, language, gender: rep });
      setSound({ url: URL.createObjectURL(blob), by: speakerText(speaker).name });
    } catch {
      /* Shown under the form. */
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Who speaks"
        description="The voice a lead hears, by the lead's language and the rep. This is how the settings are saved now."
      />
      <CardBody className="space-y-5">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <thead>
              <tr className="text-xs text-text-muted">
                <th scope="col" className="py-1.5 pr-3 font-medium">Greeting</th>
                {REPS.map((gender) => (
                  <th key={gender ?? "unset"} scope="col" className="px-3 py-1.5 font-medium">
                    {gender ? `${voiceGenderLabel(gender)} rep` : "Rep with nothing set"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border border-t border-border">
              {VOICEMAIL_LANGUAGES.map((code) => (
                <tr key={code}>
                  <th scope="row" className="py-2 pr-3 font-medium text-text">{languageName(code)}</th>
                  {REPS.map((gender) => {
                    const speaker = speakerFor(settings, code, gender);
                    const said = speakerText(speaker);
                    return (
                      <td key={gender ?? "unset"} className="px-3 py-2 align-top">
                        <span className={cn("font-medium", speaker?.voice_id ? "text-text" : "text-text-muted")}>
                          {said.name}
                        </span>
                        {said.standIn && (
                          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
                            <Badge tone="warning" size="sm">Stand-in</Badge>
                            {said.standIn}
                          </span>
                        )}
                        {!speaker?.voice_id && (
                          <span className="mt-0.5 block text-xs text-text-muted">No voice is set.</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ownVoicesText(settings) && (
          // People who recorded their own voice (ADR-117 in platform) are outside the table.
          <p className="text-xs text-text-muted">{ownVoicesText(settings)}</p>
        )}

        <form onSubmit={(event) => void speak(event)} className="space-y-3 border-t border-border pt-4" noValidate>
          <p className="text-sm font-medium text-text">Hear a sentence</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="The lead's language" htmlFor="voice-test-language">
              <Select value={language} onValueChange={pickLanguage}>
                <SelectTrigger id="voice-test-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOICEMAIL_LANGUAGES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {languageName(code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="The rep" htmlFor="voice-test-rep">
              <Select
                value={rep ?? "unset"}
                onValueChange={(value) => setRep(value === "unset" ? null : (value as VoiceGender))}
              >
                <SelectTrigger id="voice-test-rep">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPS.map((gender) => (
                    <SelectItem key={gender ?? "unset"} value={gender ?? "unset"}>
                      {gender ? `${voiceGenderLabel(gender)} rep` : "Rep with nothing set"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field
            label="Sentence"
            htmlFor="voice-test-text"
            hint="ElevenLabs charges your account for each character spoken."
            error={touched && problem ? problem : undefined}
            action={
              <span className={cn("text-xs tabular-nums", length > VOICE_TEST_MAX ? "text-danger-11" : "text-text-muted")}>
                {length} / {VOICE_TEST_MAX}
              </span>
            }
          >
            <Textarea rows={3} value={text} onChange={(event) => setText(event.target.value)} />
          </Field>
          {test.error && (
            <Callout tone="danger">
              {test.error instanceof ApiError ? test.error.message : "The sentence could not be spoken. Please try again."}
            </Callout>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="outline" loading={test.isPending} disabled={unsaved || nobody}>
              <Play />
              Speak
            </Button>
            {unsaved ? (
              <p className="text-xs text-text-muted">Save first. The test speaks with the settings as they are saved.</p>
            ) : nobody ? (
              <p className="text-xs text-text-muted">Pick a voice and save. Then there is somebody to speak.</p>
            ) : (
              <p className="text-xs text-text-muted">
                {speakerText(speaker).name} speaks for {repName(rep)} to {languageName(language) === "English" ? "an" : "a"}{" "}
                {languageName(language)} lead.
              </p>
            )}
          </div>
          {sound && (
            <div className="flex flex-wrap items-center gap-3">
              {/* Keyed by the address, so a new sound is a new player that starts by itself. */}
              <audio key={sound.url} src={sound.url} controls autoPlay aria-label="The sentence, spoken" className="h-9 w-72 max-w-full" />
              <span className="text-xs text-text-muted">Spoken by {sound.by}.</span>
            </div>
          )}
        </form>
      </CardBody>
    </Card>
  );
}
